import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    BarChart3,
    Eye,
    Navigation,
    Route,
    Calendar,
    ArrowUpRight,
    ArrowDownRight,
    Search,
    TrendingUp
} from "lucide-react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription
} from "@/components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { getAuthHeader, authenticatedFetch } from "@/lib/authHelpers";
import { useI18n } from "@/lib/i18n";

interface ShopStatsProps {
    shopId: string;
    shopName: string;
}

interface StatsData {
    period: string;
    startDate: string;
    stats: {
        profileViews: number;
        profileNavigations: number;
        routeNavigations: number;
        totalPublicRoutes: number;
        totalPrivateRoutes: number;
        newPublicRoutes: number;
        newPrivateRoutes: number;
    };
    trends: {
        profileViewsPrev: number;
    };
}

async function fetchShopStats(shopId: string, period: string): Promise<StatsData> {
    const headers = await getAuthHeader();
    const response = await authenticatedFetch(`/api/analytics/shop/${shopId}?period=${period}`, {
        ...(headers ? { headers } : {})
    });
    if (!response.ok) throw new Error("Failed to fetch stats");
    return response.json();
}

export function ShopStats({ shopId, shopName }: ShopStatsProps) {
    const { t } = useI18n();
    const [period, setPeriod] = useState("this_month");

    const { data, isLoading, error } = useQuery({
        queryKey: ["shop-stats", shopId, period],
        queryFn: () => fetchShopStats(shopId, period),
        refetchOnWindowFocus: false
    });

    if (isLoading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} className="h-24 w-full rounded-xl" />
                ))}
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="text-center py-8 text-muted-foreground">
                <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-20" />
                <p>{t("shopStats.loadFailed")}</p>
            </div>
        );
    }

    const { stats, trends } = data;

    // Calculate percentage change for views
    const viewChange = trends.profileViewsPrev > 0
        ? Math.round(((stats.profileViews - trends.profileViewsPrev) / trends.profileViewsPrev) * 100)
        : null;

    const StatCard = ({ title, value, icon: Icon, trend, subtext }: any) => (
        <Card className="overflow-hidden border-none shadow-sm bg-muted/30">
            <CardContent className="p-4">
                <div className="flex justify-between items-start">
                    <div className="p-2 bg-background rounded-lg shadow-sm">
                        <Icon className="h-4 w-4 text-primary" />
                    </div>
                    {trend !== undefined && trend !== null && (
                        <div className={`flex items-center text-xs font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {trend >= 0 ? <ArrowUpRight className="h-3 w-3 mr-0.5" /> : <ArrowDownRight className="h-3 w-3 mr-0.5" />}
                            {Math.abs(trend)}%
                        </div>
                    )}
                </div>
                <div className="mt-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
                    <h3 className="text-2xl font-bold mt-0.5">{value}</h3>
                    {subtext && <p className="text-[10px] text-muted-foreground mt-1">{subtext}</p>}
                </div>
            </CardContent>
        </Card>
    );

    return (
        <div className="space-y-6 mt-4 animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-bold flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-primary" />
                        {t("shopStats.titleFor", { shopName })}
                    </h3>
                    <p className="text-sm text-muted-foreground">{t("shopStats.desc")}</p>
                </div>
                <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger className="w-[160px] h-9">
                        <Calendar className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Periode" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="this_month">{t("shopStats.thisMonth")}</SelectItem>
                        <SelectItem value="last_month">{t("shopStats.lastMonth")}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    title="Profiel Bekeken"
                    value={stats.profileViews}
                    icon={Eye}
                    trend={viewChange}
                    subtext={period === 'this_month' ? "Sinds begin van de maand" : "Hele vorige maand"}
                />
                <StatCard
                    title="Directe Navigatie"
                    value={stats.profileNavigations}
                    icon={Navigation}
                    subtext="Vanaf je winkelprofiel"
                />
                <StatCard
                    title="Route Inclusies"
                    value={stats.totalPublicRoutes + stats.totalPrivateRoutes}
                    icon={Route}
                    subtext={`${stats.totalPublicRoutes} openbaar, ${stats.totalPrivateRoutes} privé`}
                />
                <StatCard
                    title="Route Navigatie"
                    value={stats.routeNavigations}
                    icon={Search}
                    subtext="Vanaf gekozen routes"
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-none shadow-sm bg-primary/5">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-bold flex items-center gap-2">
                            <Route className="h-4 w-4" /> {t("shopStats.routeImpact")}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-muted-foreground">{t("shopStats.newPublicRoutes")}</span>
                                <Badge variant="secondary" className="bg-primary/10 text-primary border-none">
                                    +{stats.newPublicRoutes}
                                </Badge>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-muted-foreground">{t("shopStats.newPrivateRoutes")}</span>
                                <Badge variant="secondary" className="bg-primary/10 text-primary border-none">
                                    +{stats.newPrivateRoutes}
                                </Badge>
                            </div>
                            <p className="text-[11px] text-muted-foreground border-t pt-3 mt-1 italic">
                                {t("shopStats.routeImpactHint")}
                            </p>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-none shadow-sm bg-muted/20 flex flex-col justify-center p-6 italic text-sm text-muted-foreground">
                    "Statistieken helpen je begrijpen wat klanten zoeken. Zorg voor goede foto's op je profiel om het aantal navigatie-klikken te verhogen!"
                </Card>
            </div>
        </div>
    );
}
